import React, { useCallback, useEffect, useState } from "react";
import Header from "../header";
import SearchField from "../../searchField";
import Button from "../../Button";
import { FiPlus } from "react-icons/fi";
import TransfersTable from "../tables/Transfers-Tables/transfersTable";
import Modal from "../../modal";
import Input from "../../Input";
import DateInput from "../../dateInput";
import SelectInput from "../../selectInput";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPayment } from "../../../redux/payment/action";
import { validateTransferData } from "../../../validation/validateTransfer";
import dayjs from "dayjs";
import {
  AddTransfers,
  DeleteTransfers,
  GetTransfers,
  UpdateTransfers,
} from "../../../redux/Transfers/action";
import useInfiniteScroll from "../../Hooks/useInfiniteScroll";
import { generatePopup } from "../../../utility/popup";

const Transfers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // New state to track edit mode
  const [transferData, setTransferData] = useState({
    date: "",
    amount: "",
    sourcePaymentMethod: "",
    destinationPaymentMethod: "",
  });
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
  });

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);

  const [searchValue, setSearchValue] = useState(""); // State for search input
  const [errors, setErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);

  // Other states
  const [paymentPage, setPaymentPage] = useState(20);

  const openModal = () => {
    setIsEditMode(false); // Set edit mode to false when opening for a new transfer
    setIsOpen(true);
    setErrors({});
    resetForm();
  };
  const closeModal = () => {
    setErrors({});
    resetForm();
    setIsOpen(false);
  };

  const openDeleteModal = (rowIndex) => {
    setCurrentRow(rowIndex);
    setIsDeleteOpen(true);
  };
  const closeDeleteModal = () => setIsDeleteOpen(false);

  const getPaymentData = useSelector((state) => state?.Payment?.getPayment);
  const getTransferData = useSelector(
    (state) => state?.Transfer?.transfersData?.data
  );

  const paymentOption = getPaymentData?.data?.map((e) => {
    return {
      lable: e?.paymentmethod,
      value: e?._id,
    };
  });

  const fetchMoreData = useCallback(() => {
    if (hasMoreData) {
      setFilter((prev) => ({ ...prev, limit: prev.limit + 10 }));
    }
  }, [hasMoreData]);

  const lastElementRef = useInfiniteScroll(fetchMoreData);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTransferData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (isSubmit) {
      const validationErrors = validateTransferData({
        ...transferData,
        [name]: value,
      });
      setErrors(validationErrors);
    }
  };

  const handleDateChange = (date, dateString) => {
    const updatedTransferData = {
      ...transferData,
      date: dateString,
    };

    setTransferData(updatedTransferData);

    if (isSubmit) {
      const validationErrors = validateTransferData(updatedTransferData);
      setErrors(validationErrors);
    }
  };

  const handleSubmit = async () => {
    setIsSubmit(true);
    const validationErrors = validateTransferData(transferData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      // Handle the submission, e.g., make an API call to add the transfer
      let obj = {
        date: transferData?.date,
        amount: Number(transferData?.amount),
        sourcepaymentmethod: transferData?.sourcePaymentMethod,
        receiverpaymentmethod: transferData?.destinationPaymentMethod,
      };

      if (isEditMode) {
        await dispatch(
          UpdateTransfers({ id: currentRow?._id, data: obj })
        ).then((res) => {
          if (res?.status === 200) {
            dispatch(
              GetTransfers({
                page: filter?.page,
                limit: filter?.limit,
                search: searchValue,
              })
            );
            setLoading(false);
          }
          if (res?.data?.status === 401) {
            setLoading(false);
            navigate("/");
          } else if (res?.data?.status === 400) {
            generatePopup("error", res?.data?.message);
          }

        });
      } else {
        await dispatch(AddTransfers(obj)).then((res) => {
          if (res?.status === 200) {
            dispatch(
              GetTransfers({
                page: filter?.page,
                limit: filter?.limit,
                search: searchValue,
              })
            );
            setLoading(false);
          }
          if (res?.data?.status === 401) {
            setLoading(false);
            navigate("/");
          }
          if (res?.data?.status === 400) {
            generatePopup("error", res?.data?.message);
          }
        });
      }
      setLoading(false);
      closeModal();
      resetForm();
    }
  };

  const handleDeleteTransfers = async () => {
    setLoading(true);
    await dispatch(DeleteTransfers(currentRow?._id)).then((res) => {
      if (res?.status === 200) {
        dispatch(
          GetTransfers({
            page: filter?.page,
            limit: filter?.limit,
            search: searchValue,
          })
        );
        setLoading(false);
      } else if (res?.data?.status === 400) {
        generatePopup("error", res?.data?.message);
      }
      if (res?.data?.status === 401) {
        setLoading(false);
        navigate("/");
      }
      setLoading(false);
      closeDeleteModal();
    });
    setLoading(false);
  };

  const handleEditTransfer = (row) => {
    setTransferData({
      date: dayjs(row?.date).format("DD/MM/YYYY"),
      amount: row?.amount,
      sourcePaymentMethod: row?.sourcepaymentmethod?._id,
      destinationPaymentMethod: row?.receiverpaymentmethod?._id,
    });
    setCurrentRow(row);
    setIsEditMode(true); // Set edit mode to true when editing
    setIsOpen(true); // Open the modal
  };

  useEffect(() => {
    dispatch(
      getPayment({
        page: 1,
        limit: 10,
        search: "",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      GetTransfers({
        page: filter?.page,
        limit: filter?.limit,
        search: searchValue,
      })
    ).then((res) => {
      if (res?.data?.status === 401) {
        navigate("/");
      }
      if (res?.data?.length === 0) {
        setHasMoreData(false);
      }
    });
  }, [dispatch, navigate, filter, searchValue]);

  const resetForm = () => {
    setTransferData({
      date: "",
      amount: "",
      sourcePaymentMethod: "",
      destinationPaymentMethod: "",
    });
  };

  const fetchMoreOptions = useCallback(() => {
    dispatch(
      getPayment({ page: 1, limit: paymentPage, search: searchValue })
    ).then((res) => {
      if (res?.status === 200) {
        setPaymentPage((prev) => prev + 10);
      }
    });
  }, [dispatch, paymentPage, getPaymentData, searchValue]);

  return (
    <div className="w-full px-8 py-2 flex flex-col gap-[30px]">
      <Header title={"Transfers"} />

      <div className="flex items-center justify-end gap-5">
        <SearchField
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
        <Button
          onClick={openModal}
          className={"bg-green hover:bg-darkgreen text-white"}
          icon={<FiPlus />}
          children={"Add New Transfer"}
        />
      </div>
      <Modal
        loading={loading}
        modalWidth="w-1/2"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        openModal={openModal}
        closeModal={closeModal}
        imgSrc="/images/addTransfer.svg"
        title={isEditMode ? "Edit Transfer" : "Add New Transfer"}
        btn1="Cancel"
        btn2={isEditMode ? "Update Transfer" : "Add Transfer"}
        btnClass={"justify-end"}
        handleBtn2={handleSubmit}
        modalContent={
          <div className="grid grid-cols-2 gap-x-5 gap-y-5 text-base font-normal">
            <div>
              <DateInput
                onChange={handleDateChange}
                value={
                  transferData.date
                    ? dayjs(transferData.date, "DD/MM/YYYY")
                    : null
                }
                name={"date"}
              />
              {errors.date && (
                <div className="text-red-500 text-sm">{errors.date}</div>
              )}
            </div>

            <div>
              <Input
                lableName={"Amount"}
                inputType={"number"}
                currency={true}
                name="amount"
                value={transferData.amount}
                onChange={handleInputChange}
              />
              {errors.amount && (
                <div className="text-red-500 text-sm">{errors.amount}</div>
              )}
            </div>

            <div>
              <SelectInput
                lableName={"Source Payment Method"}
                name={"sourcePaymentMethod"}
                option={paymentOption}
                value={transferData.sourcePaymentMethod}
                onChange={handleInputChange}
                fetchMoreOptions={fetchMoreOptions}
                lastElementRef={lastElementRef}
              />
              {errors.sourcePaymentMethod && (
                <div className="text-red-500 text-sm">
                  {errors.sourcePaymentMethod}
                </div>
              )}
            </div>

            <div>
              <SelectInput
                lableName={"Destination Payment Method"}
                name={"destinationPaymentMethod"}
                option={paymentOption}
                value={transferData.destinationPaymentMethod}
                onChange={handleInputChange}
                fetchMoreOptions={fetchMoreOptions}
                lastElementRef={lastElementRef}
              />
              {errors.destinationPaymentMethod && (
                <div className="text-red-500 text-sm">
                  {errors.destinationPaymentMethod}
                </div>
              )}
            </div>
          </div>
        }
      />

      <TransfersTable
        loading={loading}
        getTransferData={getTransferData}
        lastElementRef={lastElementRef}
        setIsDeleteOpen={setIsDeleteOpen}
        isDeleteOpen={isDeleteOpen}
        closeDeleteModal={closeDeleteModal}
        openDeleteModal={openDeleteModal}
        handleDeleteTransfers={handleDeleteTransfers}
        handleEditTransfer={handleEditTransfer}
      />
    </div>
  );
};

export default Transfers;
